import React from "react"
import { Box, Button, Flex, Heading, Text, IconButton } from "@radix-ui/themes"
import { Printer, ArrowLeft, Download } from "lucide-react"
import { useNavigate } from "react-router-dom"
import TurboLogo from "../../TurboLogo"

interface ReportLayoutProps {
    title: string
    subtitle?: string
    restaurantName?: string
    dateRange?: string
    children: React.ReactNode
    onBack: () => void
}

const ReportLayout: React.FC<ReportLayoutProps> = ({
    title,
    subtitle,
    restaurantName,
    dateRange,
    children,
    onBack,
}) => {
    const handlePrint = () => {
        window.print()
    }

    return (
        <Box className="min-h-screen bg-gray-50 dark:bg-gray-900 print:bg-white print:min-h-0">
            {/* Non-printable Action Bar */}
            <Box className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 print:hidden shadow-sm">
                <Flex justify="between" align="center" className="max-w-5xl mx-auto">
                    <Button variant="ghost" onClick={onBack} color="gray">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Dashboard
                    </Button>
                    <Flex gap="3">
                        <Button onClick={handlePrint} color="blue" variant="soft">
                            <Printer className="w-4 h-4 mr-2" />
                            Print Report
                        </Button>
                        <Button onClick={handlePrint} color="indigo" variant="solid">
                            <Download className="w-4 h-4 mr-2" />
                            Save as PDF
                        </Button>
                    </Flex>
                </Flex>
            </Box>

            {/* Printable Report Container */}
            <Box className="p-8 print:p-0 max-w-5xl mx-auto">
                <Box className="bg-white dark:bg-gray-800 print:dark:bg-white rounded-xl shadow-sm print:shadow-none p-8 print:p-0 border border-gray-200 print:border-none print:m-0">

                    {/* Official Report Header */}
                    <Flex justify="between" align="start" mb="8" className="border-b-2 border-gray-800 pb-6 print:pb-4">
                        <Box>
                            <Heading size="6" className="text-gray-900 print:text-black mb-1">
                                {title}
                            </Heading>
                            {subtitle && (
                                <Text size="3" color="gray" className="print:text-gray-600 block mb-3">
                                    {subtitle}
                                </Text>
                            )}
                            <Flex gap="4" mt="4">
                                <Box>
                                    <Text size="1" weight="bold" color="gray" className="uppercase tracking-wider block print:text-gray-500">
                                        Restaurant
                                    </Text>
                                    <Text size="2" weight="medium" className="text-gray-900 print:text-black">
                                        {restaurantName || "Turbo Restaurant"}
                                    </Text>
                                </Box>
                                <Box pl="4" className="border-l border-gray-200 print:border-gray-300">
                                    <Text size="1" weight="bold" color="gray" className="uppercase tracking-wider block print:text-gray-500">
                                        Period
                                    </Text>
                                    <Text size="2" weight="medium" className="text-gray-900 print:text-black">
                                        {dateRange || new Date().toLocaleDateString()}
                                    </Text>
                                </Box>
                                <Box pl="4" className="border-l border-gray-200 print:border-gray-300">
                                    <Text size="1" weight="bold" color="gray" className="uppercase tracking-wider block print:text-gray-500">
                                        Generated
                                    </Text>
                                    <Text size="2" weight="medium" className="text-gray-900 print:text-black">
                                        {new Date().toLocaleString()}
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>

                        <Flex direction="column" align="end">
                            <TurboLogo width={40} height={32} color="#DC2626" />
                            <Text size="2" weight="bold" mt="2" className="text-red-600 print:text-black">
                                TURBO POS
                            </Text>
                            <Text size="1" color="gray" className="print:text-gray-500">
                                Official Analytical Record
                            </Text>
                        </Flex>
                    </Flex>

                    {/* Report Body */}
                    <Box className="print:text-black print:[&_*]:text-black print:[&_*]:border-gray-300">
                        {children}
                    </Box>

                    {/* Report Footer */}
                    <Box mt="8" pt="4" className="border-t border-gray-200 print:border-gray-300 text-center">
                        <Text size="1" color="gray" className="print:text-gray-500">
                            Generated by Turbo Admin Portal • End of Report
                        </Text>
                    </Box>
                </Box>
            </Box>

            {/* Global Print Styles explicitly hiding extra react-dom insertions */}
            <style>{`
        @media print {
          title { display: none; }
          body { background: white !important; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          @page { margin: 1cm; size: A4; }
          ::-webkit-scrollbar { display: none; }
          
          /* Radix Table Print Fixes: Disable overflow and manage page breaks */
          .rt-TableRoot { 
            overflow: visible !important; 
            box-shadow: none !important; 
            display: table !important; 
            width: 100% !important;
          }
          .rt-TableRootTable { 
            display: table !important;
            width: 100% !important; 
            min-width: 100% !important; 
          }
          .rt-TableRow {
            display: table-row !important;
            page-break-inside: avoid;
          }
          .rt-TableCell, .rt-TableColumnHeaderCell {
            display: table-cell !important;
            page-break-inside: avoid;
          }
          table { width: 100% !important; page-break-inside: auto; border-collapse: collapse; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          td, th { page-break-inside: avoid; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
        }
      `}</style>
        </Box>
    )
}

export default ReportLayout
